import { getMpiTier } from "@/lib/mpiTiers";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Crown, Sparkles, Star, Trophy, Clock, Target } from "lucide-react";
import { useRef } from "react";

import { FormDisplay } from "./FormDisplay";
import { QuickShareButton } from "./QuickShareButton";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Tooltip,
  TooltipProps,
} from "recharts";

export interface PressureConfig {
  WIN_RELIEF: number;
  DRAW_PRESSURE: number;
  LOSS_PRESSURE: number;
  HEAVY_DEFEAT_BONUS: number;
  DECAY_FACTOR: number;
  POSITION_WEIGHT: number;
  HONEYMOON_MATCHES: number;
  HONEYMOON_RATE: number;
  BASE_FLOOR: number;
  LEGEND_FLOOR: number;
  LEGEND_TENURE_YEARS: number;
  MAX_PRESSURE: number;
}

export const DEFAULT_PRESSURE_CONFIG: PressureConfig = {
  WIN_RELIEF: -2,
  DRAW_PRESSURE: 2,
  LOSS_PRESSURE: 6,
  HEAVY_DEFEAT_BONUS: 3,
  DECAY_FACTOR: 0.95,
  POSITION_WEIGHT: 0.5,
  HONEYMOON_MATCHES: 5,
  HONEYMOON_RATE: 0.5,
  BASE_FLOOR: 15,
  LEGEND_FLOOR: 5,
  LEGEND_TENURE_YEARS: 5,
  MAX_PRESSURE: 80,
};

interface PressureDataPoint {
  matchNumber: number;
  matchDate: string;
  hotSeatScore: number;
  pressure: number;
  result: 'W' | 'D' | 'L';
  goalsFor: number;
  goalsAgainst: number;
  isHeavyDefeat: boolean;
  position: number | null;
  expectedPosition: number | null;
  positionDelta: number;
  pressureDelta: number;
  recentForm: string;
  season: string;
  // Opponent context
  opponentPosition: number | null;
  opponentName: string | null;
  isHome: boolean;
  expectedResult: 'W' | 'D' | 'L' | null;
  resultVsExpectation: 'better' | 'expected' | 'worse' | null;
}

interface SeasonMarker {
  matchNumber: number;
  season: string;
}

export interface PressureGaugeCardProps {
  assignmentId: string;
  managerId: string;
  label?: string;
  wasSacked?: boolean;
  customConfig?: Partial<PressureConfig>;
  fromDate?: string;
  toDate?: string;
  isManagerActive?: boolean;
}

// Custom tooltip component for the chart
const PressureTooltip = ({ active, payload }: TooltipProps<number, string> & { payload?: any[] }) => {
  if (!active || !payload?.length) return null;
  
  const data = payload[0].payload as PressureDataPoint;
  
  const resultColors = {
    W: 'bg-green-500',
    D: 'bg-yellow-500',
    L: 'bg-red-500',
  };

  const resultLabels = {
    W: 'Win',
    D: 'Draw',
    L: 'Loss',
  };

  const expectationColors = {
    better: 'text-green-700 dark:text-green-400 bg-green-500/10',
    expected: 'text-yellow-700 dark:text-yellow-400 bg-yellow-500/10',
    worse: 'text-red-700 dark:text-red-400 bg-red-500/10',
  };

  const expectationLabels = {
    better: '↑ Better than expected',
    expected: '= As expected',
    worse: '↓ Worse than expected',
  };

  // Format form string as colored dots
  const formCircles = <FormDisplay form={data.recentForm} variant="dots" size="md" />;

  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-xs space-y-2 min-w-[220px]">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Match {data.matchNumber}</span>
        <span className="text-muted-foreground">{new Date(data.matchDate).toLocaleDateString()}</span>
      </div>
      
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded text-white font-medium ${resultColors[data.result]}`}>
          {resultLabels[data.result]}
        </span>
        <span className="font-semibold">{data.goalsFor} - {data.goalsAgainst}</span>
        {data.isHeavyDefeat && (
          <Badge variant="destructive" className="text-[10px]">Heavy Defeat</Badge>
        )}
      </div>

      {/* Opponent Context Section */}
      {(data.opponentName || data.opponentPosition !== null) && (
        <div className="border-t pt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" />
              {data.opponentName ? (
                <>vs {data.opponentName} ({data.isHome ? 'H' : 'A'})</>
              ) : (
                <>vs {getOrdinal(data.opponentPosition!)} place</>
              )}
            </span>
            {data.expectedResult && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${resultColors[data.expectedResult]} text-white`}>
                Exp: {data.expectedResult}
              </span>
            )}
          </div>
          {data.opponentPosition !== null && data.opponentName && (
            <div className="text-[10px] text-muted-foreground">
              {getOrdinal(data.opponentPosition)} in table
            </div>
          )}
          {data.resultVsExpectation && (
            <div className={`px-2 py-1 rounded text-[10px] font-medium ${expectationColors[data.resultVsExpectation]}`}>
              {expectationLabels[data.resultVsExpectation]}
            </div>
          )}
        </div>
      )}
      
      <div className="flex items-center gap-1">
        <span className="text-muted-foreground">Last 5:</span>
        <div className="flex gap-0.5">{formCircles}</div>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Position:</span>
        <span>
          {data.position ?? '-'}
          <span className="text-muted-foreground ml-1">(exp {data.expectedPosition})</span>
        </span>
      </div>
      
      <div className="flex items-center justify-between border-t pt-2">
        <span className="text-muted-foreground">Pressure Δ:</span>
        <span className={data.pressureDelta >= 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}>
          {data.pressureDelta >= 0 ? '+' : ''}{data.pressureDelta}
          {data.pressureDelta >= 0 ? ' ↑' : ' ↓'}
        </span>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="font-medium">MPI:</span>
        <span className="font-bold">{data.hotSeatScore}</span>
      </div>
    </div>
  );
};

// Helper function for ordinal numbers
const getOrdinal = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};


// Create a stable key from config for React Query
const getConfigKey = (config?: Partial<PressureConfig>): string => {
  if (!config) return 'default';
  return JSON.stringify(config);
};

export function PressureGaugeCard({ 
  assignmentId, 
  managerId, 
  label, 
  wasSacked, 
  customConfig,
  fromDate,
  toDate,
  isManagerActive = true
}: PressureGaugeCardProps) {
  const configKey = getConfigKey(customConfig);
  const cardRef = useRef<HTMLDivElement>(null);
  
  const useCache = configKey === 'default'; // Only use cache for default config
  
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['pressure-gauge', assignmentId, configKey],
    queryFn: async () => {
      // Cache-first: check pressure_gauge_cache table before calling edge function
      if (useCache) {
        const { data: cached } = await supabase
          .from('pressure_gauge_cache')
          .select('pressure_data, calculated_at')
          .eq('assignment_id', assignmentId)
          .single();
        
        if (cached) {
          const cacheAge = Date.now() - new Date(cached.calculated_at).getTime();
          const ONE_HOUR = 60 * 60 * 1000;
          if (cacheAge < ONE_HOUR) {
            return cached.pressure_data as Record<string, unknown>;
          }
        }
      }

      // Cache miss or stale — fall back to edge function
      const { data, error } = await supabase.functions.invoke('compute-pressure-gauge', {
        body: { 
          assignmentId, 
          managerId, 
          config: customConfig,
        }
      });
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const score = (data?.currentScore as number) ?? 0;
  const managerName = (data?.managerName as string) ?? '';
  const clubName = (data?.clubName as string) ?? '';
  const dataPoints = ((data?.dataPoints || []) as PressureDataPoint[]);
  const seasonMarkers = ((data?.seasonMarkers || []) as SeasonMarker[]);
  const summary = data?.summary as {
    pressure: number;
    position: number | null;
    currentPosition: number | null;  // Live current position
    expectedPosition: number;
    expectedPositionSource: string;
    positionDelta: number;
    recentForm: string;
    formSummary: { wins: number; draws: number; losses: number };
    tenure: string;
    tenureDays: number;
    isLegend: boolean;
    titlesWonDuringTenure: number;
    floor: number;
  } | undefined;

  // Calculate tenure years for display
  const tenureYears = summary?.tenureDays ? Math.floor(summary.tenureDays / 365) : 0;
  const isLongTenure = tenureYears >= 7;
  const hasWonTitle = (summary?.titlesWonDuringTenure ?? 0) > 0;

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="w-full border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Error loading {label}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error?.message || 'Unknown error'}</p>
        </CardContent>
      </Card>
    );
  }

  const isUnicorn = score < 5;

  const getScoreColor = (score: number) => {
    if (isUnicorn) return 'text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500';
    return getMpiTier(score).tailwindText;
  };

  const getScoreBg = (score: number) => {
    if (isUnicorn) return 'bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-purple-500/10 border-purple-500/50 animate-unicorn-glow';
    return getMpiTier(score).tailwindBorder;
  };

  // Unicorn Status Badge Component
  const UnicornBadge = () => (
    <div className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 text-white text-xs font-bold shadow-lg">
      <Star className="h-3 w-3 animate-sparkle" fill="currentColor" />
      <span>UNICORN STATUS</span>
      <Sparkles className="h-3 w-3 animate-sparkle" style={{ animationDelay: '0.5s' }} />
      {/* Orbiting sparkle */}
      <div className="absolute -inset-1 animate-sparkle-spin pointer-events-none">
        <Sparkles className="absolute -top-1 left-1/2 h-2 w-2 text-yellow-300 animate-sparkle" />
      </div>
    </div>
  );

  // Long Tenure Badge Component (7+ years but no title)
  const LongTenureBadge = () => (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-md">
      <Clock className="h-3 w-3" />
      <span>LONG TENURE</span>
      <span className="text-amber-200">({tenureYears}+ years)</span>
    </div>
  );

  // Titles Won Badge Component
  const TitlesBadge = ({ count }: { count: number }) => (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs font-bold shadow-md">
      <Trophy className="h-3 w-3" />
      <span>{count} {count === 1 ? 'TITLE' : 'TITLES'}</span>
    </div>
  );
  const FormBadges = ({ form }: { form: string }) => (
    <FormDisplay form={form} variant="letters" size="sm" />
  );


  return (
    <Card className={`w-full border-2 ${getScoreBg(score)}`} ref={cardRef}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {managerName}
              {isLongTenure && !isUnicorn && <Clock className="h-4 w-4 text-amber-700 dark:text-amber-400" />}
              {summary?.isLegend && !isUnicorn && <Crown className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />}
              {isUnicorn && <Sparkles className="h-5 w-5 text-purple-500 animate-sparkle" />}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{clubName}</p>
            
            {/* Status Badges */}
            <div className="mt-2 flex flex-wrap gap-2">
              {isUnicorn && <UnicornBadge />}
              {isLongTenure && !isUnicorn && <LongTenureBadge />}
              {hasWonTitle && <TitlesBadge count={summary?.titlesWonDuringTenure ?? 0} />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && (
              <Badge variant="outline" className="text-xs animate-pulse">
                Updating...
              </Badge>
            )}
            <QuickShareButton
              elementRef={cardRef as React.RefObject<HTMLElement>}
              title={`${managerName} - MPI`}
              fileName={`${managerName.replace(/\s+/g, "-")}-mpi`}
              shareText={`${managerName}'s MPI: ${score}/100 🔥 #Football #ManagerTable`}
              size="icon"
              variant="ghost"
              iconOnly
            />
            <Badge variant="outline" className="text-xs">
              MPI
            </Badge>
            {wasSacked !== undefined && (
              <Badge variant={wasSacked ? "destructive" : "secondary"}>
                {wasSacked ? "SACKED" : "SURVIVED"}
              </Badge>
            )}
            <div className={`text-3xl font-bold ${getScoreColor(score)}`}>
              {score}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats - Compact inline row */}
        {summary && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span title={`Expected: ${summary.expectedPosition} (${summary.expectedPositionSource === 'avg-last-2-seasons' ? 'avg last 2 seasons' : summary.expectedPositionSource === 'avg-pre-manager' ? 'avg pre-manager' : 'default'})`}>
              <span className="text-muted-foreground">Pos</span> <span className="font-semibold">{summary.currentPosition ?? summary.position ?? '-'}</span>
              <span className="text-muted-foreground text-xs ml-1">(exp {summary.expectedPosition})</span>
            </span>
            <span className="text-muted-foreground">·</span>
            <FormBadges form={summary.recentForm} />
            <span className="text-muted-foreground">·</span>
            <span><span className="text-muted-foreground">Tenure</span> <span className="font-semibold">{summary.tenure}</span></span>
          </div>
        )}


        {/* Trend Chart with Tooltip and Opponent Quality Indicators */}
        {dataPoints.length > 0 && (
          <div className="-mx-6 h-40 sm:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dataPoints} margin={{ top: 5, right: 0, bottom: 5, left: -15 }}>
                <defs>
                  <linearGradient id={`pressureGradient-${assignmentId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="matchNumber" 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => `M${v}`}
                />
                <YAxis 
                  domain={[0, 100]} 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => `${v}`}
                />
                <Tooltip content={<PressureTooltip />} />
                <ReferenceLine y={50} stroke="hsl(var(--muted-foreground))" strokeDasharray="5 5" opacity={0.5} />
                <ReferenceLine y={70} stroke="hsl(var(--destructive))" strokeDasharray="3 3" opacity={0.5} />
                {seasonMarkers.map((marker) => (
                  <ReferenceLine 
                    key={marker.matchNumber}
                    x={marker.matchNumber} 
                    stroke="hsl(var(--primary))" 
                    strokeDasharray="3 3"
                    opacity={0.7}
                  />
                ))}
                <Area
                  type="monotone"
                  dataKey="hotSeatScore"
                  stroke="none"
                  fill={`url(#pressureGradient-${assignmentId})`}
                />
                <Line 
                  type="monotone" 
                  dataKey="hotSeatScore" 
                  stroke="hsl(var(--destructive))" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: "hsl(var(--destructive))" }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
